import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Wallet, Layers, Sparkles, Languages, TrendingUp, Trophy } from "lucide-react";
import Seo from "@/components/Seo";

export default function Portfolio() {
  const { user, loading: authLoading } = useAuth();
  const { format } = useCurrency();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get("/collection/portfolio-summary", { params: { status: "owned" } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [user]);

  if (authLoading) return <div className="text-center text-slate-400 py-32 text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (loading) return <div className="text-center text-slate-400 py-32 text-sm">{t("common.loading", "Loading…")}</div>;

  const empty = !data || data.owned_count === 0;
  const totalEur = data?.total_value_eur || 0;

  return (
    <div className="page-enter max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
      <Seo
        title="My Portfolio · Cartonomie"
        description="Your full Pokémon TCG collection — total value, breakdowns by set, rarity and language."
      />

      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#E11D48] mb-2 flex items-center gap-2">
            <Wallet className="w-4 h-4" /> {t("portfolio.eyebrow", "Mon portfolio")}
          </div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight text-slate-900">
            {t("portfolio.title", "Ma collection")}
          </h1>
          <p className="text-slate-500 mt-2 text-sm">
            {t("portfolio.subtitle", "Vue d'ensemble de toutes vos cartes possédées avec valeur estimée.")}
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
            {t("portfolio.total_value", "Valeur totale estimée")}
          </div>
          <div className="font-heading font-extrabold text-5xl text-[#E11D48] mt-1" data-testid="portfolio-total-value">
            {format(totalEur, "EUR")}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {data?.priced_count || 0} / {data?.owned_count || 0} {t("portfolio.priced", "cartes avec prix")}
          </div>
        </div>
      </div>

      {empty ? (
        <div className="flat-card p-12 text-center">
          <Wallet className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h2 className="font-heading font-bold text-xl text-slate-900 mb-2">
            {t("portfolio.empty_title", "Votre collection est vide")}
          </h2>
          <p className="text-slate-500 mb-6">
            {t("portfolio.empty_desc", "Cochez les cartes que vous possédez depuis n'importe quelle page de série pour les ajouter ici.")}
          </p>
          <Link to="/sets" className="btn-primary inline-flex items-center gap-2">
            {t("portfolio.browse_sets", "Parcourir les séries")}
          </Link>
        </div>
      ) : (
        <>
          {/* KPI grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <KPI
              testid="kpi-owned"
              icon={Wallet}
              label={t("portfolio.owned", "Cartes possédées")}
              value={data.owned_count}
              accent="text-slate-900"
            />
            <KPI
              testid="kpi-unique"
              icon={Sparkles}
              label={t("portfolio.unique", "Cartes uniques")}
              value={data.owned_unique}
              accent="text-slate-900"
            />
            <KPI
              testid="kpi-sets"
              icon={Layers}
              label={t("portfolio.sets_owned", "Séries")}
              value={data.by_set.length}
              accent="text-[#2563EB]"
            />
            <KPI
              testid="kpi-avg"
              icon={TrendingUp}
              label={t("portfolio.avg_value", "Valeur moyenne / carte")}
              value={format(data.priced_count ? totalEur / data.priced_count : 0, "EUR")}
              accent="text-emerald-600"
            />
          </div>

          {/* Top assets */}
          {data.top_assets.length > 0 && (
            <section className="mb-12">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-5 h-5 text-amber-500" />
                <h2 className="font-heading font-bold text-2xl text-slate-900">
                  {t("portfolio.top_assets", "Vos cartes les plus précieuses")}
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3">
                {data.top_assets.slice(0, 12).map((c, i) => (
                  <motion.div
                    key={c.id + "-" + i}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: i * 0.03 }}
                  >
                    <Link
                      to={`/cards/${c.id}`}
                      data-testid={`portfolio-top-${c.id}`}
                      className="flat-card block overflow-hidden group hover:-translate-y-1 hover:shadow-lg transition-all relative"
                    >
                      {i < 3 && (
                        <div className={`absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
                          i === 0 ? "bg-amber-400 text-amber-900" :
                          i === 1 ? "bg-slate-300 text-slate-700" :
                          "bg-amber-700 text-amber-50"
                        }`}>
                          #{i + 1}
                        </div>
                      )}
                      <div className="aspect-[2.5/3.5] bg-slate-50 overflow-hidden">
                        {c.image ? (
                          <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        ) : <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">—</div>}
                      </div>
                      <div className="p-2.5">
                        <div className="font-heading font-bold text-xs truncate text-slate-900">{c.name}</div>
                        <div className="text-[10px] text-slate-400 truncate">{c.set_name}</div>
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-[10px] text-slate-400">x{c.quantity}</span>
                          <span className="font-bold text-sm text-[#E11D48]">{format(c.line_value_eur, "EUR")}</span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </section>
          )}

          {/* By Set */}
          <section className="mb-12">
            <div className="flex items-center gap-2 mb-4">
              <Layers className="w-5 h-5 text-[#2563EB]" />
              <h2 className="font-heading font-bold text-2xl text-slate-900">
                {t("portfolio.by_set", "Répartition par série")}
              </h2>
            </div>
            <div className="flat-card overflow-hidden">
              {data.by_set.map((s, i) => {
                const pct = totalEur > 0 ? (s.value_eur / totalEur) * 100 : 0;
                return (
                  <Link
                    key={s.set_id}
                    to={`/sets/${s.set_id}`}
                    data-testid={`portfolio-set-${s.set_id}`}
                    className="flex items-center gap-4 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition"
                  >
                    <div className="w-12 h-8 flex items-center justify-center flex-shrink-0">
                      {s.logo ? <img src={s.logo} alt={s.set_name} className="max-h-8 max-w-[48px] object-contain" /> : <Layers className="w-5 h-5 text-slate-300" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-slate-900 truncate">{s.set_name}</div>
                      <div className="w-full bg-slate-100 rounded-full h-1.5 mt-1 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#2563EB] to-[#E11D48]" style={{ width: `${Math.min(100, pct)}%` }} />
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-slate-900">{format(s.value_eur, "EUR")}</div>
                      <div className="text-[10px] text-slate-400">{s.count} {t("portfolio.cards_unit", "cartes")} · {pct.toFixed(1)}%</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>

          {/* By Rarity & Language side by side */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-amber-500" />
                <h2 className="font-heading font-bold text-2xl text-slate-900">
                  {t("portfolio.by_rarity", "Par rareté")}
                </h2>
              </div>
              <div className="flat-card overflow-hidden">
                {data.by_rarity.slice(0, 8).map((r) => (
                  <div key={r.rarity} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-0" data-testid={`portfolio-rarity-${r.rarity.replace(/\s+/g, '-')}`}>
                    <span className="flex-1 text-sm text-slate-700 truncate">{r.rarity}</span>
                    <span className="text-xs text-slate-400 mr-2">{r.count}</span>
                    <span className="font-bold text-sm text-slate-900">{format(r.value_eur, "EUR")}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-4">
                <Languages className="w-5 h-5 text-emerald-600" />
                <h2 className="font-heading font-bold text-2xl text-slate-900">
                  {t("portfolio.by_language", "Par langue")}
                </h2>
              </div>
              <div className="flat-card overflow-hidden">
                {data.by_language.map((l) => (
                  <div key={l.language} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-0" data-testid={`portfolio-lang-${l.language}`}>
                    <span className="flex-1 text-sm text-slate-700 uppercase">{l.language}</span>
                    <span className="text-xs text-slate-400 mr-2">{l.count}</span>
                    <span className="font-bold text-sm text-slate-900">{format(l.value_eur, "EUR")}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, accent = "text-slate-900", testid }) {
  return (
    <div className="flat-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-slate-400" />
        <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
      </div>
      <div className={`font-heading font-extrabold text-2xl ${accent}`}>{value}</div>
    </div>
  );
}
